import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Etiqueta } from '@/components/ui/etiqueta';
import { EstadoError } from '@/components/ui/estado-vacio';
import { CambioDePrecio } from '@/components/ui/precio';
import { Skeleton } from '@/components/ui/skeleton';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoFechaBolivia, formatoPrecio } from '@/lib/utils/formato-precio';
import { hrefDeLaEmpresa } from '@/router/href-de-la-empresa';
import { useAbreElModulo } from '@/features/auth/hooks/use-abre-el-modulo';
import { PorQueEstePrecio } from '@/features/pricing-rules/components/por-que-este-precio';
import { desdeFilaDeLaBandeja } from '../lib/explicacion-del-precio';
import { queLePasa } from '../lib/desactualizadas';
import {
  comoCambioEnPalabras,
  desdeCuandoEnPalabras,
  estrategiaEnPalabras,
  reglasEnPalabras,
} from '../lib/fila-abierta';
import { priceProposalsApi } from '../services/price-proposals-api';
import type { DetalleDeLaPropuesta, PriceProposalRow } from '../types/price-proposals.types';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';

/**
 * Spec 294 (D-R4-3): cómo la propuso su estrategia (la corrida que la creó).
 * Sin corrida conocida no se dice nada: el nombre de la estrategia ya lo dice.
 */
const COMO_LA_PROPUSO: Record<NonNullable<PriceProposalRow['origenDetalle']>, string> = {
  CADENCIA: 'Propuesta por su horario',
  COMPETENCIA: 'Propuesta por precios nuevos de la competencia',
  MANUAL: 'Propuesta al tocar «Revisar ahora»',
  DEVOLUCION: 'Propuesta al devolverle productos',
};

/**
 * La fila abierta de la bandeja (lote B1, spec 246 punto 2; maqueta `b1-fila`):
 * todo para decidir sin salir de Por decidir. A la izquierda, la estrategia
 * (con su método y su permiso), desde cuándo lo tiene, el último cambio de
 * precio y las otras reglas; abajo, un solo botón azul. A la derecha, la
 * explicación v2 de siempre (`PorQueEstePrecio`), con «En el mercado».
 *
 * La explicación sale de la fila (lo que congeló la propuesta); la izquierda,
 * de un pedido chico (`GET /:id/detalle`). Si la propuesta es de antes y no
 * congeló el mercado, se muestra el de hoy, y se dice.
 */
export function DetalleDeLaPropuestaAbierta({
  fila: f,
  trabajando,
  abriendoFicha = false,
  onAprobar,
  onDescartar,
  onDecidir,
  onVerEstrategia,
  onVerHistorial,
}: {
  fila: PriceProposalRow;
  trabajando: boolean;
  /** Mientras abre la ficha de la estrategia: el botón queda deshabilitado. */
  abriendoFicha?: boolean;
  onAprobar: (f: PriceProposalRow) => void;
  onDescartar: (f: PriceProposalRow) => void;
  onDecidir: (f: PriceProposalRow) => void;
  onVerEstrategia: (strategyApplicationId: string) => void;
  onVerHistorial: (f: PriceProposalRow) => void;
}) {
  // Soporte («Entrar como») en una demo sólo mira: no aprueba, descarta ni devuelve propuestas.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  const [detalle, setDetalle] = useState<DetalleDeLaPropuesta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);
  // Ronda E (T16-4): el enlace a Precios Dinámicos, sólo para quien lo abre.
  const abrePreciosDinamicos = useAbreElModulo('pricing');

  useEffect(() => {
    let vivo = true;
    setDetalle(null);
    setError(null);
    priceProposalsApi
      .detalle(f.id)
      .then((d) => vivo && setDetalle(d))
      .catch((e) => vivo && setError(mensajeDeError(e, 'No se pudo traer el detalle.')));
    return () => {
      vivo = false;
    };
  }, [f.id, intento]);

  const recorrido = useMemo(() => {
    const r = desdeFilaDeLaBandeja(f);
    // Una propuesta de antes no congeló el mercado: el de hoy, dicho como tal.
    if (!r.mercado && detalle?.mercadoDeHoy) r.mercado = { ...detalle.mercadoDeHoy, deHoy: true };
    return r;
  }, [f, detalle]);

  const pendiente = f.status === 'PENDING';
  const aElegir = pendiente && !!f.sinPrecioPosible;
  const yaNoAplica = pendiente && (f.yaNoAplica ?? (f.desactualizada || !!f.costoCambio));
  const esVuelta = f.triggerKind === 'EXPIRY';
  const delRecalculo = f.origen === 'RECALCULO';
  const e = detalle?.estrategia ?? null;
  const u = detalle?.ultimoCambio ?? null;
  const reglas = detalle ? reglasEnPalabras(detalle.reglas) : null;

  return (
    <div
      className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]"
      data-testid="detalle-de-la-propuesta"
    >
      <div className="space-y-3 text-sm">
        <dl>
          {delRecalculo ? (
            <>
              {/* La del recálculo sin estrategia: la propuso el recálculo, no una estrategia. */}
              <dt className="text-xs text-muted-foreground">Quién lo propone</dt>
              <dd className="font-semibold">El recálculo de reglas</dd>
              <dd className="text-foreground/80">
                {f.origenDelPedido === 'HOY'
                  ? 'El producto no está en ninguna estrategia: partió de su precio de hoy'
                  : 'Su estrategia ya no existe'}
              </dd>
            </>
          ) : (
            <>
              <dt className="text-xs text-muted-foreground">La estrategia que lo propone</dt>
              <dd className="font-semibold [overflow-wrap:anywhere]">
                {/* La de una corrida cuya estrategia se borró: ya no tiene estrategia. */}
                {!esVuelta && f.strategyApplicationId == null
                  ? 'Una estrategia que ya no existe'
                  : (e?.name ?? f.strategyName ?? 'Sin estrategia registrada')}
                {/* El detalle ya dice si la estrategia está cerrada (vencida o borrada). */}
                {e?.cerrada && <span className="text-foreground/80"> · ya no corre</span>}
              </dd>
              {esVuelta ? (
                <dd className="text-alerta">Vuelve a su precio: la estrategia venció</dd>
              ) : (
                e && <dd className="text-foreground/80">{estrategiaEnPalabras(e)}</dd>
              )}
              {/* Spec 294 (D-R4-3): cómo la propuso su estrategia, si se sabe. */}
              {!esVuelta && f.origenDetalle && (
                <dd className="text-foreground/80">{COMO_LA_PROPUSO[f.origenDetalle]}</dd>
              )}
            </>
          )}
          {f.recalculadaEl && (
            <dd className="text-foreground/80">
              Recalculada el {formatoFechaBolivia(f.recalculadaEl, { hora: true })} con las reglas
              de ese momento
            </dd>
          )}
        </dl>

        {error ? (
          <EstadoError onReintentar={() => setIntento((n) => n + 1)}>{error}</EstadoError>
        ) : !detalle ? (
          <div className="space-y-2" aria-label="Cargando el detalle">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-8 w-2/3" />
          </div>
        ) : (
          <dl className="space-y-3">
            {/* Ronda del QA de R4: sin estrategia que partió de hoy, arriba ya dice
                «El producto no está en ninguna estrategia»: no se repite. */}
            {!esVuelta && !(delRecalculo && !detalle.estrategia && f.origenDelPedido === 'HOY') && (
              <div>
                <dt className="text-xs text-muted-foreground">Desde cuándo</dt>
                <dd>{desdeCuandoEnPalabras(detalle, f)}</dd>
              </div>
            )}

            <div>
              {/* D10: «hora de Bolivia» se dice una vez, acá. */}
              <dt className="text-xs text-muted-foreground">
                Último cambio de precio · hora de Bolivia
              </dt>
              {u ? (
                <>
                  <dd className="flex flex-wrap items-baseline gap-x-1.5">
                    <b className="font-semibold">{formatoFechaBolivia(u.cuando, { hora: true })}</b>
                    <span className="tabular-nums">
                      · {u.antes != null ? `${formatoPrecio(u.antes)} → ` : 'nació en '}
                      {formatoPrecio(u.despues)}
                    </span>
                    {u.antes != null && <CambioDePrecio diferencia={u.despues - u.antes} />}
                  </dd>
                  <dd className="text-xs text-muted-foreground">{comoCambioEnPalabras(u)}</dd>
                </>
              ) : (
                <dd className="text-muted-foreground">
                  No quedó registrado: antes sólo se guardaban los cambios que hacían las
                  estrategias, y ninguna lo cambió.
                </dd>
              )}
              <dd>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => onVerHistorial(f)}
                >
                  Ver todo su historial
                </Button>
              </dd>
            </div>

            {f.costoHoy == null && (
              <div>
                <dt className="text-xs text-muted-foreground">Costo</dt>
                <dd className="flex flex-wrap items-center gap-2">
                  <Etiqueta tono="alerta">Sin costo</Etiqueta>
                  {abrePreciosDinamicos ? (
                    <a
                      href={hrefDeLaEmpresa('/dynamic-pricing')}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      Cargar el costo en Precios Dinámicos
                    </a>
                  ) : (
                    <span className="text-muted-foreground">
                      El costo se carga en Precios Dinámicos: pedíselo a quien lo administra.
                    </span>
                  )}
                </dd>
              </div>
            )}

            <div>
              <dt className="text-xs text-muted-foreground">Otras reglas que lo alcanzan</dt>
              <dd className={reglas ? '[overflow-wrap:anywhere]' : 'text-muted-foreground'}>
                {reglas ?? 'Ninguna regla lo alcanzó al proponer.'}
              </dd>
            </div>
          </dl>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="acciones-de-la-fila">
          {pendiente &&
            (aElegir ? (
              <Button
                size="sm"
                className="rounded-full px-4"
                disabled={trabajando}
                onClick={() => onDecidir(f)}
              >
                Decidir
              </Button>
            ) : yaNoAplica ? (
              <span className="min-w-0 text-xs [overflow-wrap:anywhere] text-muted-foreground">
                Ya no aplica: el precio o el costo cambió desde que se propuso. {queLePasa(f)}
              </span>
            ) : (
              <Button
                size="sm"
                className="rounded-full px-4"
                disabled={trabajando}
                onClick={() => onAprobar(f)}
                {...sinCambios}
              >
                {`Aprobar ${formatoPrecio(f.priceAfter)}`}
              </Button>
            ))}
          {pendiente && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={trabajando}
              onClick={() => onDescartar(f)}
              {...sinCambios}
            >
              Descartar…
            </Button>
          )}
          {e && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              disabled={abriendoFicha}
              onClick={() => onVerEstrategia(e.id)}
            >
              Ver la estrategia
            </Button>
          )}
        </div>
      </div>

      <PorQueEstePrecio recorrido={recorrido} tiempo={pendiente ? 'HOY' : 'PASO'} />
    </div>
  );
}
